import { useForm } from "react-hook-form";
import { SleepFormData } from "../SleepFormContext";
import { useEffect, useMemo } from "react";
import { useChildStore } from "@/lib/store/child";
import { useAuthStore } from "@/lib/store/auth";
import { useCaregiverStore } from "@/lib/store/caregiver";
import { logger } from "@/lib/services/logger-service";

// Enhanced form type with additional properties and methods
export interface EnhancedSleepForm extends ReturnType<typeof useForm<SleepFormData>> {
  selectedChild?: { name: string; id: string };
}

export function useSleepDialogForm(
  childId?: string,
  activityToEdit?: any,
  isEditing?: boolean
): EnhancedSleepForm {
  const { selectedChild, children } = useChildStore();
  const { caregiverId: authCaregiverId } = useAuthStore();
  const { caregiverId: profileCaregiverId } = useCaregiverStore();
  const caregiverId = authCaregiverId || profileCaregiverId;
  
  // Determine the correct child ID to use, prioritizing the explicitly passed childId
  const effectiveChildId = useMemo(() => {
    const id = childId || selectedChild?.id || '';
    
    logger.debug('Sleep dialog form: determining effective child ID', {
      providedChildId: childId,
      storeSelectedChildId: selectedChild?.id,
      effectiveChildId: id
    });
    
    return id;
  }, [childId, selectedChild?.id]);
  
  // Create the form instance
  const form = useForm<SleepFormData>({
    defaultValues: {
      start_time: new Date(),
      end_time: undefined,
      has_woken_up: 'unknown',
      status: 'in_progress',
      childId: effectiveChildId,
      caregiverId: caregiverId || '',
      confirmed: false,
      notes: '',
      location_id: '',
      environment_id: '',
      onset_method_id: '',
      restfulness_id: '',
      sound_id: '',
      sound_level_id: '',
      signs_of_sleep_debt: false
    }
  });
  
  // Update childId whenever it changes
  useEffect(() => {
    if (effectiveChildId && form.getValues('childId') !== effectiveChildId) {
      logger.debug('Updating sleep form childId', {
        from: form.getValues('childId'),
        to: effectiveChildId
      });
      form.setValue('childId', effectiveChildId);
    }
  }, [effectiveChildId, form]);

  // Update caregiverId whenever it changes
  useEffect(() => {
    if (caregiverId && form.getValues('caregiverId') !== caregiverId) {
      logger.debug('Updating sleep form caregiverId', {
        from: form.getValues('caregiverId'),
        to: caregiverId
      });
      form.setValue('caregiverId', caregiverId);
    }
  }, [caregiverId, form]);
  
  // Initialize form with edit values if this is an edit operation
  useEffect(() => {
    if (activityToEdit && isEditing) {
      logger.debug('Initializing form with activity data for editing', {
        activityId: activityToEdit.id,
        childId: activityToEdit.childId || effectiveChildId
      });
      
      const formValues: Partial<SleepFormData> = {
        notes: activityToEdit.notes || '',
        childId: activityToEdit.childId || effectiveChildId,
        caregiverId: activityToEdit.caregiver_id || caregiverId || '',
        location_id: activityToEdit.location || '',
        environment_id: activityToEdit.environment || '',
        onset_method_id: activityToEdit.onset_method || '',
        restfulness_id: activityToEdit.quality || '',
        sound_id: activityToEdit.sound || '',
        sound_level_id: activityToEdit.sound_level || '',
        signs_of_sleep_debt: activityToEdit.signs_of_sleep_debt || false,
        has_woken_up: activityToEdit.has_woken_up || (activityToEdit.endTime ? 'yes' : 'unknown'),
        status: activityToEdit.status || (activityToEdit.endTime ? 'complete' : 'in_progress')
      };
      
      // Only set dates if they exist and are valid
      if (activityToEdit.startTime) {
        try {
          const startTime = new Date(activityToEdit.startTime);
          if (!isNaN(startTime.getTime())) {
            formValues.start_time = startTime;
          }
        } catch (error) {
          console.error('Error parsing start time:', error);
        }
      }
      
      if (activityToEdit.endTime) {
        try {
          const endTime = new Date(activityToEdit.endTime);
          if (!isNaN(endTime.getTime())) {
            formValues.end_time = endTime;
          }
        } catch (error) {
          console.error('Error parsing end time:', error);
        }
      }
      
      // Reset the form with the activity values
      form.reset(formValues);
    }
  }, [activityToEdit, isEditing, effectiveChildId, caregiverId, form]);
  
  // Find the selected child from the store or props
  const currentSelectedChild = useMemo(() => {
    const targetChildId = effectiveChildId;
    if (!targetChildId) return undefined;
    
    return children.find(child => child.id === targetChildId);
  }, [effectiveChildId, children]);
  
  // Enhance the form object with additional properties
  return {
    ...form,
    selectedChild: currentSelectedChild
  };
}
